import React from "react";

import { observer } from "mobx-react-lite";

import { FlowVersion } from "src/api/flowTypes";
import { useUpdateSchema } from "src/api/flowVersionQueries";
import { getOptimisticallyUpdatedSchema } from "src/api/getOptimisticNodes";
import {
  SearchParamsComponent,
  SearchParamsTabView,
} from "src/base-components/SearchParamsTabView";
import { useCanAuthoringEditFlowVersion } from "src/hooks/useCanAuthoringEditFlowVersion";
import { refetchSchemaCompletionData } from "src/nodeEditor/api/queries";
import { SchemaOptions, URLKeys } from "src/router/SearchParams";
import { SchemaEditor } from "src/schema/schemaEditor/SchemaEditor";
import { isCaseSchema } from "src/schema/schemaEditor/utils";
import { defaultSchema } from "src/schema/schemaMappingUtils";
import {
  useInputSchemaError,
  useOutputSchemaError,
} from "src/store/runState/inputOutputErrors";
import { useGraphStore } from "src/store/StoreProvider";

type Props = {
  flowVersion: FlowVersion;
  setSchemaInvalid: (errored: boolean) => void;
};

export const SchemaTabView: React.FC<Props> = observer(
  ({ flowVersion, setSchemaInvalid }) => {
    const { graphHasChanged } = useGraphStore();
    const inputSchema = flowVersion.input_schema ?? defaultSchema;
    const inputSchemaError = useInputSchemaError(inputSchema);
    const outputSchemaError = useOutputSchemaError();
    const canEditFlowVersion = useCanAuthoringEditFlowVersion();

    const updateSchemaMutation = useUpdateSchema(flowVersion.id, () => {
      graphHasChanged();
      refetchSchemaCompletionData(flowVersion.id);
    });

    const hasCaseInInput = isCaseSchema(inputSchema);

    const outputSchema = flowVersion.output_schema ?? defaultSchema;
    const isOutputSchemaConfigured =
      outputSchema.properties &&
      Object.keys(outputSchema.properties).length > 0;

    const isSchemaMutating = updateSchemaMutation.isPending;

    const tabs: SearchParamsComponent[] = [
      {
        title: "Input",
        dataLoc: "schema-input-tab",
        searchParamValue: SchemaOptions.Input,
        component: (
          <SchemaEditor
            key={`${flowVersion.id}-input`}
            flowVersionId={flowVersion.id}
            immutable={!canEditFlowVersion}
            isOutputSchemaConfigured={isOutputSchemaConfigured}
            isSchemaMutating={isSchemaMutating}
            mutateSchema={updateSchemaMutation.mutate}
            schema={
              isSchemaMutating
                ? inputSchema
                : getOptimisticallyUpdatedSchema(
                    flowVersion.id,
                    inputSchema,
                    "input",
                  )
            }
            setSchemaInvalid={setSchemaInvalid}
            type={SchemaOptions.Input}
          />
        ),
        errored: inputSchemaError,
      },
      {
        title: "Output",
        dataLoc: "schema-output-tab",
        searchParamValue: SchemaOptions.Output,
        disabled: hasCaseInInput
          ? "Decision Flows using a Case as input do not have an Output."
          : false,
        component: (
          <SchemaEditor
            key={`${flowVersion.id}-output`}
            flowVersionId={flowVersion.id}
            immutable={!canEditFlowVersion}
            isSchemaMutating={isSchemaMutating}
            mutateSchema={updateSchemaMutation.mutate}
            schema={
              isSchemaMutating
                ? outputSchema
                : getOptimisticallyUpdatedSchema(
                    flowVersion.id,
                    outputSchema,
                    "output",
                  )
            }
            setSchemaInvalid={setSchemaInvalid}
            type={SchemaOptions.Output}
          />
        ),
        errored: outputSchemaError,
      },
    ];
    return (
      <SearchParamsTabView
        containerClassName="pr-10"
        defaultValue={SchemaOptions.Input}
        searchParamKey={URLKeys.Schema}
        tabs={tabs}
      />
    );
  },
);
